import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { RhyBusCard } from '@rhybus/card'
import { RhyBusButton } from '@rhybus/button'
import { RhyBusAlert } from '@rhybus/alert'
import { RhyBusFileUpload } from '@rhybus/file-upload'
import { RhyBusCustomSelect, RhyBusInputText, RhyBusTextArea, RhyBusDatePicker } from '@rhybus/form-controls'
import DynamicFormRenderer from '../../formengine/dynamic-form-renderer'
import { loadPublishedSchema } from '../../formengine/publish-utils'
import Instructions from '../../utils/controls/instructions/instructions'
import PageOverlay from '../../utils/controls/pageoverLay/pageoverLay'
import { RhyBusValidationSummary } from '@rhybus/validation-summary'
import { ListNames, STATUS_CODE, trainingfields,trainingtrackfields, traininguploadfields } from '../../config/configuration'
import { rhybusRListsHttp } from '../../api/rhybus-list-http'
import { useForm } from '../../services/form'
import { useFormMetadataOptions } from '../../services/metadataManagement'
import { useTooltipResolver } from '../../services/use-tooltip-resolver'
import StudentTable from './form-table'
import { getTrainingTrackLinesByGUID } from '../../services/listview'
import { uploadFilesToCaseBlobs, softDeleteBlobFiles, getFilesForDetail, getDetailFileContent } from '../../services/detailview'

const headerColumns = [
  { key: 'training', label: 'Training Sequence ID' },
  { key: 'suffix', label: 'Suffix' },
  { key: 'tMasl', label: 'T-MASL' },
  { key: 'location', label: 'Location' },
  { key: 'exa', label: 'EXA' },
  { key: 'priority', label: 'Priority' },
  { key: 'reportDate', label: 'Report Date' },
  { key: 'startDate', label: 'Start Date' },
  { key: 'endDate', label: 'End Date' },
  { key: 'duration', label: 'Duration' },
  { key: 'classId', label: 'Class ID' },
  { key: 'status', label: 'Status' },
  { key: 'financialStatus', label: 'Financial Status' },
  { key: 'facilityCategory', label: 'Facility Category' },
]

export default function CreateForm() {
    const { itemGuid } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const copyState = (location.state as any)?.isCopied ? (location.state as any) : null
    const { formRef, values, setValues, handleChange, handleCancel, showCancelAlert, setShowCancelAlert, showInstructions, setShowInstructions, validationMsgs, setValidationMsgs, focusField, position, formHydrationVersion, handleTrainingTrackSave, handleTrainingTrackSubmit, validateTrainingTrackDraft, loadTrainingTrackFormByGUID, prepareForCopy, currentStatusCode, hasTrainingTrackValueChanges } = useForm('training') as any
    const publishedSchema = loadPublishedSchema('training')
    const resolveTooltip = useTooltipResolver()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const isSubmittingRef = useRef(false)
    const [loading, setLoading] = useState(!!(copyState || itemGuid))
    const [showNoChangesAlert, setShowNoChangesAlert] = useState(false)
    const [trainingLines, setTrainingLines] = useState<any[]>([])
    const [originalTrainingLines, setOriginalTrainingLines] = useState<any[]>([])
    const [trainingLineInput, setTrainingLineInput] = useState<Record<string, any>>({})
    const [showSuccessAlert, setShowSuccessAlert] = useState(false)
    const [showDraftSuccessAlert, setShowDraftSuccessAlert] = useState(false)
    const [showErrorAlert, setShowErrorAlert] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [newTrainingGuid, setNewTrainingGuid] = useState('')
    const [lineResetKey, setLineResetKey] = useState(0)
    //const [tableResetKey, setTableResetKey] = useState(0)
    const [trackLineErrors, setTrackLineErrors] = useState<Record<string, string>>({})
    const fileMapRef = useRef<Record<string, File[]>>({})
    const uploadFileCacheRef = useRef<Map<string, File>>(new Map())
    const attachmentsRef = useRef<any[]>([])
    const removedAttachmentsRef = useRef<any[]>([])
    const { isLoading: metadataLoading, branchOptions, divisionOptions, productOptions, tbmOptions, priorityOptions, rankOptions, trackTypeOptions, trainingEnrollmentStatusOptions, financialStatusOptions, facilityCategoryOptions,wcnOptions, exaOptions, suffixOptions, quarterOptions } = useFormMetadataOptions()

    const [userCaseIdOptions, setUserCaseIdOptions] = useState<{ label: string; value: string; fiscalYear?: number | null; endFiscalYear?: number | null }[]>([])
    const [allCaseLines, setAllCaseLines] = useState<{ CaseGUID: string; CaseMasterLineGUID: string; UserCaseLine: string }[]>([])
    const [lineOptions, setLineOptions] = useState<{ label: string; value: string }[]>([])
    const selectVal = (v: any): string =>
        v && typeof v === 'object' && 'value' in v ? String(v.value) : String(v || '')

    const formatDate = (v: any): string => {
        if (!v) return ''
        const d = v instanceof Date ? v : typeof v.toDate === 'function' ? v.toDate() : null
        if (d && !isNaN(d.getTime())) {
            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}T12:00:00.000Z`
        }
        return String(v)
    }

    const validateLineDates = (reportDate: any, startDate: any, endDate: any): Record<string, string> => {
        const errs: Record<string, string> = {}
        const hasAnyDate = !!(reportDate || startDate || endDate)
        if (hasAnyDate) {
            if (!reportDate) errs.reportDate = 'Report Date is required when Start or End Date is provided'
            if (!startDate) errs.startDate = 'Start Date is required when Report or End Date is provided'
            if (!endDate) errs.endDate = 'End Date is required when Report or Start Date is provided'
        }
        if (startDate && endDate) {
            const toDate = (v: any) => { const d = v instanceof Date ? v : new Date(String(v)); return isNaN(d.getTime()) ? null : d }
            const start = toDate(startDate)
            const end = toDate(endDate)
            if (start && end && end < start) errs.endDate = 'End Date must be on or after Start Date'
        }
        return errs
    }

    const handleTrainingLineAdd = () => {
        const errors: Record<string, string> = {}
        dynamicTrainingTrackFields.forEach(field => {
            if (!field.isMandatory || field.type === 'button') return
            const val = trainingLineInput[field.key]
            const isEmpty = field.type === 'select'
                ? !selectVal(val)
                : !(val?.toString().trim())
            if (isEmpty) errors[field.key] = `${field.label} is required`
        })
        Object.assign(errors, validateLineDates(trainingLineInput.reportDate, trainingLineInput.startDate, trainingLineInput.endDate))
        if (Object.keys(errors).length > 0) {
            setTrackLineErrors(errors)
            return
        }
        setTrackLineErrors({})
        const newRow = {
            id: Date.now(),
            training: String(trainingLines.length + 1).padStart(3, '0'),
            suffix: selectVal(trainingLineInput.suffix),
            tMasl: selectVal(trainingLineInput.tmasl),
            location: trainingLineInput.location || '',
            exa: selectVal(trainingLineInput.exa),
            priority: selectVal(trainingLineInput.priority),
            reportDate: formatDate(trainingLineInput.reportDate),
            startDate: formatDate(trainingLineInput.startDate),
            endDate: formatDate(trainingLineInput.endDate),
            duration: [trainingLineInput.duration, selectVal(trainingLineInput.durationType)].filter(Boolean).join(' '),
            classId: trainingLineInput.classId || '',
            status: selectVal(trainingLineInput.status),
            financialStatus: selectVal(trainingLineInput.financialStatus),
            facilityCategory: selectVal(trainingLineInput.facilityCategory),
        }
        setTrainingLines(prev => [...prev, newRow])
        setTrainingLineInput({})
        setLineResetKey(k => k + 1)
    }

    useEffect(() => {
        rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER).then(async list => {
            const items = [...(await list.query().get<any>())]
            const opts = items
                .filter((i: any) => i.UserCaseID && i.StatusCode !== STATUS_CODE.SAVED)
                .map((i: any) => ({
                    label: i.UserCaseID as string,
                    value: i.CaseGUID as string,
                    fiscalYear: i.FiscalYear ?? null,
                    endFiscalYear: i.EndFiscalYear ?? null,
                }))
            setUserCaseIdOptions(opts)
        })
        rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER_LINE).then(async list => {
            const items = [...(await list.query().get<any>())]
            setAllCaseLines(items.map((i: any) => ({ CaseGUID: i.CaseGUID as string, CaseMasterLineGUID: i.CaseMasterLineGUID as string, UserCaseLine: i.UserCaseLine as string })))
        })
    }, [])

    useEffect(() => {
        const selected = values.userCaseId
        const caseGUID = typeof selected === 'object' ? selected?.value : selected
        if (!caseGUID) { setLineOptions([]); return }
        const lines = allCaseLines
            .filter(l => l.CaseGUID === caseGUID && l.UserCaseLine)
            .map(l => ({ label: l.UserCaseLine, value: l.CaseMasterLineGUID }))
        if (lines.length === 0) {
            const na = { label: 'Not Applicable', value: 'NA' }
            setLineOptions([na])
            setValues((prev: any) => ({ ...prev, line: na }))
        } else if (lines.length === 1) {
            setLineOptions(lines)
            setValues((prev: any) => {
                const currentLine = prev.line
                const currentVal = typeof currentLine === 'object' ? currentLine?.value : currentLine
                if (!currentVal) return { ...prev, line: lines[0] }
                return prev
            })
        } else {
            setLineOptions(lines)
        }
    }, [values.userCaseId, allCaseLines])

    // Copy To: load source record fields + lines + attachments, then strip source identifiers
    useEffect(() => {
        if (!copyState) return
        const sourceGUID = copyState.copiedData?.ItemGUID ?? copyState.copiedData?.trainingTrackGUID ?? copyState.copiedData?.TrainingTrackGUID
        if (!sourceGUID) { setLoading(false); return }
        loadTrainingTrackFormByGUID(sourceGUID).then((sourceItemKey: string | null | undefined) => {
            // Copy training lines (strip DB identifiers so they are treated as new on submit)
            const linesPromise = getTrainingTrackLinesByGUID(sourceGUID).then((lines: any[]) => {
                if (lines.length) {
                    setTrainingLines(lines.map(({ eKey: _eKey, eTag: _eTag, ...rest }: any) => rest))
                    setOriginalTrainingLines([])
                }
            })
            // Pre-fetch attachment binaries into cache so uploadAttachmentsAfterSubmit can find them
            if (sourceItemKey) {
                getFilesForDetail(sourceItemKey, 'training-detail-view').then((fileStructure: any) => {
                    const preloaded = (fileStructure?.allFilesList ?? [])
                        .filter((f: any) => f.metadata?.blobContainerName === 'training-form')
                        .map((f: any) => ({ ...f, name: f.Name || f.metadata?.name || '' }))
                    preloaded.forEach(async (f: any) => {
                        try {
                            const content = await getDetailFileContent(sourceItemKey, { Name: f.Name, folderName: f.folderName }, 'training-detail-view')
                            if (content.byteLength > 0) {
                                uploadFileCacheRef.current.set(`${f.name}_${f.size}`, new File([content], f.name, { type: f.metadata?.type || '' }))
                            }
                        } catch { /* non-fatal: file simply won't be re-uploaded */ }
                    })
                })
            }
            prepareForCopy()
            return linesPromise
        }).finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!itemGuid) return
        Promise.all([
            loadTrainingTrackFormByGUID(itemGuid),
            getTrainingTrackLinesByGUID(itemGuid).then(lines => {
                if (lines.length) {
                    setTrainingLines(lines)
                    setOriginalTrainingLines(lines)
                }
            })
        ]).finally(() => setLoading(false))
    }, [itemGuid, loadTrainingTrackFormByGUID])

    // Seed attachmentsRef once when server-side attachments first arrive (edit mode).
    // After this point onFileChange keeps the ref current on every user interaction.
    const hasSeededAttachmentsRef = useRef(false)
    useEffect(() => {
        if (!hasSeededAttachmentsRef.current && (values.attachments?.length ?? 0) > 0) {
            attachmentsRef.current = values.attachments
            hasSeededAttachmentsRef.current = true
        }
    }, [values.attachments])

    useEffect(() => {
        const captureDrop = (e: DragEvent) => {
            const files = e.dataTransfer?.files
            if (!files?.length) return
            Array.from(files).forEach((f) => uploadFileCacheRef.current.set(`${f.name}_${f.size}`, f))
        }
        document.addEventListener('drop', captureDrop, true)
        return () => document.removeEventListener('drop', captureDrop, true)
    }, [])

    const dynamicFields = useMemo(() =>
        trainingfields.map(f => {
            if (f.key === 'branch' && branchOptions.length) return { ...f, options: branchOptions }
            if (f.key === 'division' && divisionOptions.length) return { ...f, options: divisionOptions }
            if (f.key === 'product' && productOptions.length) return { ...f, options: productOptions }
            if (f.key === 'TBM' && tbmOptions.length) return { ...f, options: tbmOptions }
            if (f.key === 'priority' && priorityOptions.length) return { ...f, options: priorityOptions }
            if (f.key === 'rank' && rankOptions.length) return { ...f, options: rankOptions }
            if (f.key === 'type' && trackTypeOptions.length) return { ...f, options: trackTypeOptions }
            if (f.key === 'wcn' && wcnOptions.length) return { ...f, options: wcnOptions }
            if (f.key === 'quarter' && quarterOptions.length) return { ...f, options: quarterOptions }
            if (f.key === 'userCaseId') return { ...f, options: userCaseIdOptions, onChange: (v: any) => { setValues((prev: any) => ({ ...prev, userCaseId: v, line: null })); setValidationMsgs((prev: any) => prev.filter((m: any) => m.key !== 'userCaseId' && m.key !== 'line')) } }
            if (f.key === 'line') return { ...f, options: lineOptions, onChange: (v: any) => { setValues((prev: any) => ({ ...prev, line: v })); setValidationMsgs((prev: any) => prev.filter((m: any) => m.key !== 'line')) } }
            return f
        }),
        [branchOptions, divisionOptions, productOptions, tbmOptions, priorityOptions, rankOptions, trackTypeOptions, wcnOptions, quarterOptions, userCaseIdOptions, lineOptions]
    )

    const dynamicTrainingTrackFields = useMemo(() =>
        trainingtrackfields.map(f => {
            if (f.key === 'branch' && branchOptions.length) return { ...f, options: branchOptions }
            if (f.key === 'division' && divisionOptions.length) return { ...f, options: divisionOptions }
            if (f.key === 'product' && productOptions.length) return { ...f, options: productOptions }
            if (f.key === 'TBM' && tbmOptions.length) return { ...f, options: tbmOptions }
            if (f.key === 'status' && trainingEnrollmentStatusOptions.length) return { ...f, options: trainingEnrollmentStatusOptions }
            if (f.key === 'financialStatus' && financialStatusOptions.length) return { ...f, options: financialStatusOptions }
            if (f.key === 'facilityCategory' && facilityCategoryOptions.length) return { ...f, options: facilityCategoryOptions }
            if (f.key === 'priority' && priorityOptions.length) return { ...f, options: priorityOptions }
            if (f.key === 'exa' && exaOptions.length) return { ...f, options: exaOptions }
            if (f.key === 'suffix' && suffixOptions.length) return { ...f, options: suffixOptions }

            return f
        }),
        [branchOptions, divisionOptions, productOptions, tbmOptions, trainingEnrollmentStatusOptions, financialStatusOptions, facilityCategoryOptions,priorityOptions, exaOptions, suffixOptions]
    )

    const registerFieldElement = (fieldKey: string, el: any) => {
        const prev = formRef.current[fieldKey]
        formRef.current[fieldKey] = { ...(typeof prev === 'object' && prev ? prev : {}), el }
    }

    const captureActualFiles = (fieldKey: string, e: any) => {
        const target = e.target as HTMLInputElement
        if (target?.type === 'file' && target.files) {
            const files = Array.from(target.files)
            fileMapRef.current[fieldKey] = files
            files.forEach((f) => uploadFileCacheRef.current.set(`${f.name}_${f.size}`, f))
        }
    }

    const onFileChange = (fieldKey: string, strippedFiles: any[]) => {
        const actualFiles = fileMapRef.current[fieldKey] ?? []
        const merged = strippedFiles.map((stripped: any) => {
            if (!stripped || stripped instanceof File) return stripped
            const match = actualFiles.find(
                (f: File) => f.name === (stripped.name || stripped.FileName) && f.size === stripped.size
            )
            return match ?? stripped
        })

        if (fieldKey === 'attachments') {
            const previous = attachmentsRef.current
            removedAttachmentsRef.current = previous.filter(
                (p: any) => !merged.some((m: any) => (m.Name || m.name) === (p.Name || p.name))
            )
            attachmentsRef.current = merged
        }

        fileMapRef.current[fieldKey] = []
        handleChange(fieldKey, merged)
    }

    const uploadAttachmentsAfterSubmit = async (result: any) => {
        const itemKey = result?.itemKey ?? result?.response?.eKey ?? (isActuallyEditMode ? itemGuid : null)
        const formType = 'training'
        const caseGUID = result?.guid ?? itemGuid ?? null
        const pageName = 'training-detail-view'
        const blobContainerName = itemKey ? `${pageName}/${itemKey}` : pageName

        if (removedAttachmentsRef.current.length > 0 && itemKey) {
            await softDeleteBlobFiles(itemKey, removedAttachmentsRef.current, pageName)
            removedAttachmentsRef.current = []
        }

        const filesToUpload = (values.attachments ?? [])
            .map((f: any) => {
                if (f instanceof File) return f
                return uploadFileCacheRef.current.get(`${f.name}_${f.size}`) ?? null
            })
            .filter((f: File | null): f is File => f !== null)

        console.log({
            formType,
            caseGUID,
            itemKey,
            attachments: filesToUpload.map((f: File) => f.name),
            pageName,
            blobContainerName,
            uploadStarted: filesToUpload.length > 0 && !!itemKey,
            uploadCompleted: false,
            uploadError: null,
        })

        if (!filesToUpload.length || !itemKey) return

        try {
            const uploadResult = await uploadFilesToCaseBlobs(itemKey, filesToUpload, undefined, 'training-form', pageName)
            console.log({
                formType,
                caseGUID,
                itemKey,
                attachments: filesToUpload.map((f: File) => f.name),
                pageName,
                blobContainerName,
                uploadStarted: true,
                uploadCompleted: uploadResult,
                uploadError: uploadResult ? null : 'One or more files failed to upload',
            })
            filesToUpload.forEach((f: File) => uploadFileCacheRef.current.delete(`${f.name}_${f.size}`))
        } catch (err) {
            console.error('[uploadAttachmentsAfterSubmit]', err)
            console.log({
                formType,
                caseGUID,
                itemKey,
                attachments: filesToUpload.map((f: File) => f.name),
                pageName,
                blobContainerName,
                uploadStarted: true,
                uploadCompleted: false,
                uploadError: String(err),
            })
        }
    }

    const isResumeDraft = !!itemGuid && Number(currentStatusCode) === 1
    const isActuallyEditMode = !!itemGuid && !isResumeDraft
    const formTitle = isActuallyEditMode ? 'Edit Training Track' : 'Create Training  Track'
    const isSubmitted = currentStatusCode !== null && Number(currentStatusCode) >= STATUS_CODE.SUBMITTED
    // When editing an existing record, wait until currentStatusCode is loaded before showing Save/Submit/Update
    const buttonsReady = !itemGuid || currentStatusCode !== null
    const showSaveAsDraft = !isSubmitted
    const cancelNavPath = isActuallyEditMode ? `/training-detail-view/${itemGuid}` : '/trainings'
    const primaryActionLabel = 'Save as Draft'
    const primaryActionIcon = 'icon-rb-saved color-primary'
    const submitActionLabel = isSubmitted ? 'Update' : 'Submit'
    const submitActionIcon = isSubmitted ? 'icon-rb-update' : 'icon-rb-checked'

    const handleResult = (result: any, onSuccess: (guid: string) => void) => {
        if (result && (result as any).success) {
            onSuccess((result as any).guid)
        } else if (result && (result as any).error) {
            setErrorMessage((result as any).error)
            setShowErrorAlert(true)
        }
    }

    const submitActionHandlerTop = async () => {
        if (isSubmittingRef.current) return
        if (isActuallyEditMode && !hasTrainingTrackValueChanges() && !linesChanged() && !attachmentsChanged()) {
            setShowNoChangesAlert(true)
            return
        }
        isSubmittingRef.current = true
        setIsSubmitting(true)
        try {
            const result = await handleTrainingTrackSubmit('top', trainingLines, originalTrainingLines)
            if (result && (result as any).success) await uploadAttachmentsAfterSubmit(result)
            handleResult(result, guid => { setNewTrainingGuid(guid); setShowSuccessAlert(true) })
        } finally {
            isSubmittingRef.current = false
            setIsSubmitting(false)
        }
    }
    const submitActionHandlerBottom = async () => {
        if (isSubmittingRef.current) return
        if (isActuallyEditMode && !hasTrainingTrackValueChanges() && !linesChanged() && !attachmentsChanged()) {
            setShowNoChangesAlert(true)
            return
        }
        isSubmittingRef.current = true
        setIsSubmitting(true)
        try {
            const result = await handleTrainingTrackSubmit('bottom', trainingLines, originalTrainingLines)
            if (result && (result as any).success) await uploadAttachmentsAfterSubmit(result)
            handleResult(result, guid => { setNewTrainingGuid(guid); setShowSuccessAlert(true) })
        } finally {
            isSubmittingRef.current = false
            setIsSubmitting(false)
        }
    }
    const linesChanged = () => {
        const strip = (lines: any[]) => lines.map(({ id: _id, ...rest }) => rest)
        return JSON.stringify(strip(trainingLines)) !== JSON.stringify(strip(originalTrainingLines))
    }

    const attachmentsChanged = () =>
        uploadFileCacheRef.current.size > 0 || removedAttachmentsRef.current.length > 0

    const handleCancelClick = () => {
        if (hasTrainingTrackValueChanges() || linesChanged() || attachmentsChanged()) {
            handleCancel()
        } else {
            navigate(cancelNavPath)
        }
    }

    const saveActionHandlerTop = async () => {
        if (isSubmittingRef.current) return
        if (!validateTrainingTrackDraft('top')) return
        if (itemGuid && !hasTrainingTrackValueChanges() && !linesChanged() && !attachmentsChanged()) {
            setShowNoChangesAlert(true)
            return
        }
        isSubmittingRef.current = true
        setIsSubmitting(true)
        try {
            const result = await handleTrainingTrackSave('top', trainingLines, originalTrainingLines)
            if (result && (result as any).success) await uploadAttachmentsAfterSubmit(result)
            handleResult(result, guid => { setNewTrainingGuid(guid); setShowDraftSuccessAlert(true) })
        } finally {
            isSubmittingRef.current = false
            setIsSubmitting(false)
        }
    }
    const saveActionHandlerBottom = async () => {
        if (isSubmittingRef.current) return
        if (!validateTrainingTrackDraft('bottom')) return
        if (itemGuid && !hasTrainingTrackValueChanges() && !linesChanged() && !attachmentsChanged()) {
            setShowNoChangesAlert(true)
            return
        }
        isSubmittingRef.current = true
        setIsSubmitting(true)
        try {
            const result = await handleTrainingTrackSave('bottom', trainingLines, originalTrainingLines)
            if (result && (result as any).success) await uploadAttachmentsAfterSubmit(result)
            handleResult(result, guid => { setNewTrainingGuid(guid); setShowDraftSuccessAlert(true) })
        } finally {
            isSubmittingRef.current = false
            setIsSubmitting(false)
        }
    }

    return (<>
        {(loading || metadataLoading || isSubmitting) && <PageOverlay />}
        <RhyBusAlert
            show={showCancelAlert}
            size="md"
            variant="confirm"
            message={'Are you sure you want to cancel? Unsaved changes will be lost.'}
            confirmLabel="Yes"
            cancelLabel="No"
            okIcon="icon-rb-checked"
            cancelIcon="icon-rb-close"
            onConfirm={() => {
                setShowCancelAlert(false)
                navigate(cancelNavPath)
            }}
            onCancel={() => setShowCancelAlert(false)}
        />
        <RhyBusAlert
            size="sm"
            show={showNoChangesAlert}
            variant="confirm"
            message="No changes detected"
            confirmLabel="OK"
            onConfirm={() => setShowNoChangesAlert(false)}
        />
        <RhyBusAlert
            show={showDraftSuccessAlert}
            size="md"
            variant="success"
            title="Success"
            message="Training draft saved successfully."
            autoClose={3000}
            onClose={() => {
                setShowDraftSuccessAlert(false)
                navigate('/trainings', { state: { activeTab: 'saved', trainingTrackGUID: newTrainingGuid || itemGuid } })
            }}
        />
       <RhyBusAlert
            show={showSuccessAlert}
            size="md"
            variant="success"
            title="Success"
            message={isActuallyEditMode? 'Training updated successfully.': 'Training submitted successfully'}
            autoClose={3000}
            onClose={() => {
                setShowSuccessAlert(false)
                navigate(`/training-detail-view/${newTrainingGuid || itemGuid}`)
            }}
        />
        <main id="main-content">
        <div className="container">
            <div className="row">
                <div className="col-12">
                    <RhyBusCard
                        cardSize='lg'
                        isTitleBorder={true}
                        cardHeaderLeft={
                            <div className="d-flex align-items-center gap-2">
                                <h2 className='inter-semibold font-18' tabIndex={0} aria-label={formTitle}><span className="icon-rb-create-form color-primary font-14 me-1" aria-hidden="true"></span>{formTitle}</h2>
                                <RhyBusButton style={{'--rb-btn-bg': '#2D3A91', fontWeight: '400'}} variant="primary" label="Instructions" className="font-12 inter-medium" rightIcon={showInstructions ? 'icon-rb-arrow-up font-12' : 'icon-rb-arrow-down font-12'} size="sm" onClick={() => setShowInstructions((s: boolean) => !s)} />
                            </div>
                        }
                        cardHeaderRight={
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <RhyBusButton variant="primary" label="Cancel" leftIcon="icon-rb-close font-11 color-primary" onClick={handleCancelClick} style={{ '--rb-btn-text': '#030332', '--rb-btn-hover-bg': '#fff', '--rb-btn-bg': '#fff', '--rb-btn-border': '#C0CAD8', '--rb-btn-hover-border': '#C0CAD8', fontWeight: 600 }} />
                                {buttonsReady && showSaveAsDraft && <RhyBusButton variant="primary" label={primaryActionLabel} leftIcon={primaryActionIcon} onClick={saveActionHandlerTop} style={{ '--rb-btn-text': '#030332', '--rb-btn-hover-bg': '#fff', '--rb-btn-bg': '#fff', '--rb-btn-border': '#C0CAD8', '--rb-btn-hover-border': '#C0CAD8', fontWeight: 600 }} />}
                                {buttonsReady && <RhyBusButton variant="primary" label={submitActionLabel} leftIcon={submitActionIcon} onClick={submitActionHandlerTop} style={{ fontWeight: 600 }} />}
                            </div>
                        }
                        cardbodyclassName='pt-0'
                        cardBody={
                            <>
                                <div className="row">
                                    <div className='col-12'>
                                        {/* Instructions and Form Validations */}
                                        {showInstructions && <Instructions />}
                                        {validationMsgs && validationMsgs.length > 0 && position === 'top' && (
                                            <div className='mb-3'>
                                                <RhyBusValidationSummary
                                                    errors={validationMsgs.map((m: any) => ({ name: m.key, label: m.text }))}
                                                    variant="info"
                                                    showClose={true}
                                                    onClose={() => setValidationMsgs([])}
                                                    onErrorClick={(error: any) => focusField(error.name)}
                                                />
                                            </div>
                                        )}
{/* FORM_ENGINE_START */}
{publishedSchema ? (
  <DynamicFormRenderer schema={publishedSchema} values={values} handleChange={handleChange} customHandleChange={(field:any,value:any)=>{if(field.onChange){field.onChange(value); return}handleChange(field.key,value)}} formRef={formRef} formHydrationVersion={formHydrationVersion} />
) : (
                                        <div className="row align-items-end">
                                            {dynamicFields.map(field => (
                                                <div className={field.colClass} key={field.key}>
                                                    {field.type === 'select' && (
                                                        <RhyBusCustomSelect
                                                            id={field.key}
                                                            label={field.label}
                                                            options={field.options}
                                                            value={values[field.key] || null}
                                                            onChange={(v: any) => (field as any).onChange ? (field as any).onChange(v) : handleChange(field.key, v)}
                                                            ref={(inst: any) => {
                                                                const prev = formRef.current[field.key] || {}
                                                                const dom = inst && (inst.el ?? inst) || null
                                                                try { if (dom && dom.setAttribute) dom.setAttribute('data-rb-select', field.key) } catch (e) { void e }
                                                                formRef.current[field.key] = { ...(typeof prev === 'object' && prev ? prev : {}), instance: inst, el: dom }
                                                            }}
                                                            placeholder={field.placeholder ?? ''}
                                                            isMandatory={field.isMandatory}
                                                            labelClassName='inter-medium font-14'
                                                            info={resolveTooltip((field as any).tooltipId)}
                                                            tooltipPlacement={(field.tooltipPlacement ?? 'top') as 'top' | 'bottom' | 'right' | 'left'}
                                                        />
                                                    )}
                                                    {field.type === 'textarea' && (
                                                        <RhyBusTextArea
                                                            id={field.key}
                                                            label={field.label}
                                                            placeholder={field.placeholder ?? ''}
                                                            value={values[field.key] || ''}
                                                            onChange={(e: any) => handleChange(field.key, e?.target?.value ?? e)}
                                                            rows={(field as any).rows ?? 4}
                                                            ref={(el: any) => {
                                                                const prev = formRef.current[field.key]
                                                                formRef.current[field.key] = { ...(typeof prev === 'object' && prev ? prev : {}), el }
                                                            }}
                                                            labelClassName='inter-medium font-14'
                                                            isMandatory={field.isMandatory}
                                                            info={resolveTooltip((field as any).tooltipId)}
                                                            tooltipPlacement={(field.tooltipPlacement ?? 'top') as 'top' | 'bottom' | 'right' | 'left'}
                                                        />
                                                    )}
                                                    {field.type === 'date' && (
                                                        <div ref={(el: any) => registerFieldElement(field.key, el)}>
                                                            <RhyBusDatePicker
                                                                id={field.key}
                                                                label={field.label}
                                                                value={values[field.key] || null}
                                                                onChange={(v: any) => handleChange(field.key, v)}
                                                                placeholder={field.placeholder ?? ''}
                                                                formlayout="vertical"
                                                                isMandatory={field.isMandatory}
                                                                labelClassName="inter-medium font-14"
                                                                onOpenChange={() => { }}
                                                                {...({ info: resolveTooltip((field as any).tooltipId), tooltipPlacement: field.tooltipPlacement ?? 'top' } as any)}
                                                            />
                                                        </div>
                                                    )}
                                                    {(field.type === 'text' || field.type === 'currency') && (
                                                        <RhyBusInputText
                                                            key={`${field.key}-${formHydrationVersion}`}
                                                            id={field.key}
                                                            label={field.label}
                                                            type={field.type}
                                                            placeholder={field.placeholder ?? ''}
                                                            value={values[field.key] || ''}
                                                            onChange={(e: any) => handleChange(field.key, e?.target?.value ?? e)}
                                                            ref={(el: any) => {
                                                                const prev = formRef.current[field.key]
                                                                formRef.current[field.key] = { ...(typeof prev === 'object' && prev ? prev : {}), el }
                                                            }}
                                                            labelClassName="font-14 inter-medium"
                                                             className={field.type === 'currency' ? 'text-end' : ''}
                                                            isMandatory={field.isMandatory}
                                                            info={resolveTooltip((field as any).tooltipId)}
                                                            tooltipPlacement={(field.tooltipPlacement ?? 'top') as 'top' | 'bottom' | 'right' | 'left'}
                                                        />
                                                    )}

                                                    {field.type === 'file' && (
                                                        <div onChangeCapture={(e: any) => captureActualFiles(field.key, e)}>
                                                            <RhyBusFileUpload
                                                                labelClassName='inter-medium font-14'
                                                                label={field.label}
                                                                files={values[field.key] || []}
                                                                onChange={(files: any) => onFileChange(field.key, files)}
                                                                hint="Upload the file which are in the .png, .jpeg, .xlsx,.txt .docx, .pdf files and special characters will not be used in the document names. Maximum size for file is 25 MB. Limit is up to 10 files per screen."
                                                                note="Screenshots need to be saved locally before uploading into system."
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            ))}

                                            <div className='col-12'>
                                                <div className='border-radius-2 border-1 overflow-hidden'>
                                                <div className='form-header-bgcolor whitetext font-14 px-3 py-2 inter-medium'>
                                                    Training Track Lines
                                                </div>
                                                <div className='p-3 pt-0'>
                                                <div className="row align-items-end" >
                                                {dynamicTrainingTrackFields.map(field => (
                                                <div className={field.colClass} key={field.key}>
                                                    {field.type === 'select' && (
                                                        <RhyBusCustomSelect
                                                            id={field.key}
                                                            label={field.label}
                                                            options={field.options}
                                                            value={trainingLineInput[field.key] || null}
                                                            onChange={(v: any) => {
                                                                setTrainingLineInput(prev => ({ ...prev, [field.key]: v }))
                                                                if (trackLineErrors[field.key]) setTrackLineErrors(prev => { const n = { ...prev }; delete n[field.key]; return n })
                                                            }}
                                                            error={!!trackLineErrors[field.key]}
                                                            errorMessage={trackLineErrors[field.key] || ''}
                                                            placeholder={field.placeholder ?? ''}
                                                            isMandatory={field.isMandatory}
                                                            labelClassName='inter-medium font-14'
                                                            info={resolveTooltip((field as any).tooltipId)}
                                                            tooltipPlacement={(field.tooltipPlacement ?? 'top') as 'top' | 'bottom' | 'right' | 'left'}
                                                        />
                                                    )}
                                                    {field.type === 'date' && (
                                                        <div className='p-relative' key={`${field.key}-${lineResetKey}`}>
                                                            <RhyBusDatePicker
                                                                id={field.key}
                                                                label={field.label}
                                                                value={trainingLineInput[field.key] || null}
                                                                onChange={(v: any) => {
                                                                    setTrainingLineInput(prev => ({ ...prev, [field.key]: v }))
                                                                    if (trackLineErrors[field.key]) setTrackLineErrors(prev => { const n = { ...prev }; delete n[field.key]; return n })
                                                                }}
                                                                placeholder={field.placeholder ?? ''}
                                                                formlayout="vertical"
                                                                isMandatory={field.isMandatory}
                                                                labelClassName="inter-medium font-14"
                                                                onOpenChange={() => { }}
                                                                {...({ info: resolveTooltip((field as any).tooltipId), tooltipPlacement: field.tooltipPlacement ?? 'top' } as any)}
                                                            />
                                                            {trackLineErrors[field.key] && <div className='rb-form-error-text rb-datepicker-error'>{trackLineErrors[field.key]}</div>}
                                                        </div>
                                                    )}
                                                    {(field.type === 'text' || field.type === 'currency') && (
                                                        <RhyBusInputText
                                                            key={`${field.key}-${lineResetKey}`}
                                                            id={field.key}
                                                            label={field.label}
                                                            type={field.key === 'duration' ? 'number' : field.type}
                                                            min={field.key === 'duration' ? 0 : undefined}
                                                            placeholder={field.placeholder ?? ''}
                                                            value={trainingLineInput[field.key] || ''}
                                                            onChange={(e: any) => {
                                                                setTrainingLineInput(prev => ({ ...prev, [field.key]: e?.target?.value ?? e }))
                                                                if (trackLineErrors[field.key]) setTrackLineErrors(prev => { const n = { ...prev }; delete n[field.key]; return n })
                                                            }}
                                                            error={!!trackLineErrors[field.key]}
                                                            errorMessage={trackLineErrors[field.key] || ''}
                                                            labelClassName="font-14 inter-medium"
                                                            isMandatory={field.isMandatory}
                                                            info={resolveTooltip((field as any).tooltipId)}
                                                            tooltipPlacement={(field.tooltipPlacement ?? 'top') as 'top' | 'bottom' | 'right' | 'left'}
                                                        />
                                                    )}
                                                    {field.type === 'button' && (
                                                        <RhyBusButton label={field.label} leftIcon='icon-rb-add font-11'  size="md" className='ms-auto text-nowrap' onClick={handleTrainingLineAdd} />
                                                    )}
                                                </div>
                                            ))}
                                            <div className='mt-4'>
                                                <StudentTable initialData={trainingLines} headerColumns={headerColumns} thClassname="text-nowrap" tdClassname="whitebg" fixFirstColumn={true} fixLastColumn={true} isEdit={true} onDataChange={setTrainingLines} noResultText="No records found." editFields={dynamicTrainingTrackFields.filter((f:any)=>f.type!=='button')} onValidateRow={(row: any) => validateLineDates(row.reportDate, row.startDate, row.endDate)}  />
                                            </div>
                                            </div>
                                            </div>
                                            </div>
                                            </div>

{traininguploadfields.map(field => (
                                                <div className={field.colClass} key={field.key}>
                                                    
                                                    {field.type === 'textarea' && (
                                                        <RhyBusTextArea
                                                            id={field.key}
                                                            label={field.label}
                                                            placeholder={field.placeholder ?? ''}
                                                            value={values[field.key] || ''}
                                                            onChange={(e: any) => handleChange(field.key, e?.target?.value ?? e)}
                                                            rows={(field as any).rows ?? 4}
                                                            ref={(el: any) => {
                                                                const prev = formRef.current[field.key]
                                                                formRef.current[field.key] = { ...(typeof prev === 'object' && prev ? prev : {}), el }
                                                            }}
                                                            labelClassName='inter-medium font-14'
                                                            isMandatory={field.isMandatory}
                                                            info={resolveTooltip((field as any).tooltipId)}
                                                            tooltipPlacement={(field.tooltipPlacement ?? 'top') as 'top' | 'bottom' | 'right' | 'left'}
                                                        />
                                                    )}

                                                    {field.type === 'file' && (
                                                        <div onChangeCapture={(e: any) => captureActualFiles(field.key, e)}>
                                                            <RhyBusFileUpload
                                                                labelClassName='inter-medium font-14'
                                                                label={field.label}
                                                                files={values[field.key] || []}
                                                                onChange={(files: any) => onFileChange(field.key, files)}
                                                                info={resolveTooltip((field as any).tooltipId)}
                                                                infoIconClassName={field.infoIcon}
                                                                hint="Upload the files which are in the .png, .jpeg, .xlsx,.txt .docx, .pdf files and special characters will not be used in the document names. Maximum size for file is 25MB. Limit is up to 10 files per screen."
                                                                note="Screenshots need to be saved locally before uploading into system."
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            ))}

                                        </div>
)}
{/* FORM_ENGINE_END */}

                                    </div>
                                </div>
                                {/* Section A ends*/}
                                {validationMsgs && validationMsgs.length > 0 && position === 'bottom' && (
                                    <div className='mt-3'>
                                        <RhyBusValidationSummary
                                            errors={validationMsgs.map((m: any) => ({ name: m.key, label: m.text }))}
                                            variant="info"
                                            showClose={true}
                                            onClose={() => setValidationMsgs([])}
                                            onErrorClick={(error: any) => focusField(error.name)}
                                        />
                                    </div>
                                )}
                            </>
                        }
                        cardFooter={
                            <div className='d-flex justify-content-end w-100 gap-2 flex-wrap'>
                                <RhyBusButton variant="primary" label="Cancel" leftIcon="icon-rb-close font-11 color-primary" onClick={handleCancelClick} style={{ '--rb-btn-text': '#030332', '--rb-btn-hover-bg': '#fff', '--rb-btn-bg': '#fff', '--rb-btn-border': '#C0CAD8', '--rb-btn-hover-border': '#C0CAD8', fontWeight: 600 }} />
                                {buttonsReady && showSaveAsDraft && <RhyBusButton variant="primary" label={primaryActionLabel} leftIcon={primaryActionIcon} onClick={saveActionHandlerBottom} style={{ '--rb-btn-text': '#030332', '--rb-btn-hover-bg': '#fff', '--rb-btn-bg': '#fff', '--rb-btn-border': '#C0CAD8', '--rb-btn-hover-border': '#C0CAD8', fontWeight: 600 }} />}
                                {buttonsReady && <RhyBusButton variant="primary" label={submitActionLabel} leftIcon={submitActionIcon} onClick={submitActionHandlerBottom} style={{ fontWeight: 600 }} />}
                            </div>
                        }
                    />
                </div>
            </div>
        </div>
        </main>
    </>
    )
}
